// client/src/hooks/useTransactions.js

import { useState, useCallback, useRef } from 'react';
import transactionService from '../services/transactionService';

const useTransactions = () => {
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({});
  const [amountMax, setAmountMax] = useState(0);
  const [loading, setLoading] = useState({
    accounts: false,
    transactions: false,
    analyzeCSV: false,
    importTransactions: false
  });
  const [error, setError] = useState('');
  // Filtres qui changent vite (recherche, montant, dates) : sans ce garde, une
  // réponse obsolète peut résoudre après une plus récente et écraser la liste.
  const latestTransactionsRequest = useRef(0);

  const fetchAccounts = useCallback(async () => {
    setLoading((prev) => ({ ...prev, accounts: true }));
    setError('');
    try {
      const data = await transactionService.getAccounts();
      setAccounts(data);
      return data;
    } catch (err) {
      setError(err.message || 'Impossible de charger les comptes.');
      throw err;
    } finally {
      setLoading((prev) => ({ ...prev, accounts: false }));
    }
  }, []);

  const fetchTransactions = useCallback(async (accountId, page = 1, limit = 50, filters = {}) => {
    if (!accountId) return;
    const requestId = ++latestTransactionsRequest.current;
    setLoading((prev) => ({ ...prev, transactions: true }));
    setError('');
    try {
      const {
        data,
        pagination,
        amountMax: max
      } = await transactionService.getTransactions(accountId, page, limit, filters);
      if (requestId !== latestTransactionsRequest.current) return { data, pagination };
      setTransactions(data);
      setPaginationInfo(pagination);
      if (max != null) setAmountMax(max);
      return { data, pagination };
    } catch (err) {
      if (requestId === latestTransactionsRequest.current) {
        setError(err.message || 'Impossible de charger les transactions.');
      }
      throw err;
    } finally {
      if (requestId === latestTransactionsRequest.current) {
        setLoading((prev) => ({ ...prev, transactions: false }));
      }
    }
  }, []);

  // Reclassement sur place des lignes déjà chargées : évite de refetch (et donc
  // de faire clignoter toute la liste) juste pour changer une catégorie.
  const applyCategoryLocally = useCallback((ids, categoryId) => {
    const targets = new Set(ids);
    setTransactions((prev) => prev.map((tx) => (targets.has(tx.id) ? { ...tx, categoryId } : tx)));
  }, []);

  const analyzeCSV = useCallback(async (file, separator) => {
    setLoading((prev) => ({ ...prev, analyzeCSV: true }));
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('separator', separator);
      return await transactionService.analyzeCSV(formData);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading((prev) => ({ ...prev, analyzeCSV: false }));
    }
  }, []);

  const importTransactions = useCallback(async (file, mapping, separator, accountId) => {
    setLoading((prev) => ({ ...prev, importTransactions: true }));
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(mapping));
      formData.append('separator', separator);
      formData.append('accountId', accountId);
      return await transactionService.importTransactions(formData);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading((prev) => ({ ...prev, importTransactions: false }));
    }
  }, []);

  return {
    accounts,
    transactions,
    paginationInfo,
    amountMax,
    loading,
    error,
    setError,
    fetchAccounts,
    fetchTransactions,
    applyCategoryLocally,
    analyzeCSV,
    importTransactions
  };
};

export default useTransactions;
